import React from 'react';
import { useNavigate } from 'react-router';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@redbax/ui-kit';
import { useCreatePackagePolicy } from '../api/package-policy-hooks';

const allEcosystems = ['npm', 'PyPI', 'NuGet', 'Maven', 'Go', 'crates.io', 'RubyGems'] as const;

const defaultNames: Record<string, string> = {
  npm: 'npm Security Policy',
  PyPI: 'PyPI Security Policy',
  NuGet: 'NuGet Security Policy',
  Maven: 'Maven Security Policy',
  Go: 'Go Security Policy',
  'crates.io': 'crates.io Security Policy',
  RubyGems: 'RubyGems Security Policy',
};

interface CreatePolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configuredEcosystems: string[];
}

export function CreatePolicyDialog({
  open,
  onOpenChange,
  configuredEcosystems,
}: CreatePolicyDialogProps) {
  const navigate = useNavigate();
  const createPolicy = useCreatePackagePolicy();

  const availableEcosystems = allEcosystems.filter(
    (eco) =>
      !configuredEcosystems.some(
        (configured) => configured.toLowerCase() === eco.toLowerCase(),
      ),
  );

  const [ecosystem, setEcosystem] = React.useState('');
  const [name, setName] = React.useState('');
  const [defaultAction, setDefaultAction] = React.useState('Allow');

  React.useEffect(() => {
    if (open && availableEcosystems.length > 0 && !ecosystem) {
      setEcosystem(availableEcosystems[0]);
      setName(defaultNames[availableEcosystems[0]] ?? '');
    }
  }, [open, availableEcosystems, ecosystem]);

  const handleEcosystemChange = (value: string) => {
    setEcosystem(value);
    setName(defaultNames[value] ?? `${value} Security Policy`);
  };

  const handleSubmit = () => {
    if (!ecosystem || !name.trim()) return;
    createPolicy.mutate(
      {
        ecosystem,
        name: name.trim(),
        defaultAction,
        minPackageAgeDays: 0,
        blockCriticalSeverity: true,
        blockHighSeverity: false,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setEcosystem('');
          setName('');
          setDefaultAction('Allow');
          navigate(`/package-security/policies/${encodeURIComponent(ecosystem)}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Package Policy</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {availableEcosystems.length === 0 ? (
            <p className="text-sm text-text-secondary">
              All supported ecosystems already have policies configured.
            </p>
          ) : (
            <>
              <div>
                <Label htmlFor="cp-ecosystem">Ecosystem</Label>
                <Select value={ecosystem} onValueChange={handleEcosystemChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select ecosystem" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableEcosystems.map((eco) => (
                      <SelectItem key={eco} value={eco}>
                        {eco}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cp-name">Policy Name</Label>
                <Input
                  id="cp-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cp-action">Default Action</Label>
                <Select value={defaultAction} onValueChange={setDefaultAction}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Allow">Allow</SelectItem>
                    <SelectItem value="Block">Block</SelectItem>
                    <SelectItem value="RequireApproval">Require Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {availableEcosystems.length > 0 && (
            <Button
              onClick={handleSubmit}
              disabled={!ecosystem || !name.trim() || createPolicy.isPending}
            >
              {createPolicy.isPending ? 'Creating...' : 'Create'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
